import { Injectable } from '@angular/core';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import {
  PortalNotification,
  PortalNotificationRecipient,
  PortalNotificationType,
} from '@app/core/interfaces/portal-notification';
import { PortalNotificationEnhancedRecipient } from '@app/core/interfaces/portal-notification-enhanced-recipient';
import { PortalNotificationSendTestMail } from '@app/core/interfaces/portal-notification-send-test-mail';
import { PortalNotificationTranslationOrderUpdate } from '@app/core/interfaces/portal-notification-translation-order-update';
import { PortalNotificationTranslationUpdate } from '@app/core/interfaces/portal-notification-translation-update';
import { PortalNotificationUpdate } from '@app/core/interfaces/portal-notification-update';
import { PortalNotificationWithTranslations } from '@app/core/interfaces/portal-notification-with-translations';
import { PortalPublishedNotificationUpdate } from '@app/core/interfaces/portal-published-notification-update';
import { MyPortalNotification } from '@app/core/interfaces/portal/my-portal-notification';
import { PortalCopyNotification } from '@app/core/interfaces/portal/portal-copy-notification';
import { ProductOption } from '@app/core/interfaces/product-option';
import { RenderedPortalNotification } from '@app/core/interfaces/rendered-portal-notification';
import { PortalEventContext } from '@app/core/models/portal-event-context';
import { UserLevel, userLevelIsEnough } from '@app/core/models/user-level';
import { PortalNotificationRepository } from '@app/core/repositories/portal-notification.repository';
import { ApplicationEventService, NewUnseenNotification } from '@app/core/services/application-events';
import { EventSourceService, PortalEventSubject } from '@app/core/services/event-source.service';
import { NotificationService } from '@app/core/services/notification.service';
import { MenuService } from '@app/menu/services/menu.service';
import { PortalDataService } from '@app/portal/services/portal-data.service';
import { ToastService } from '@app/portal/services/toast-service';
import { DialogService } from '@app/shared/dialog/dialog.service';
import { AuthorizationService } from '@app/shared/services/authorization.service';
import { CachedHelperService } from '@app/shared/services/cached-helper.service';
import { ContextHelperService } from '@app/shared/services/context/context-helper.service';
import { ServiceProviderContextService } from '@app/shared/services/context/service-provider-context.service';
import { SystemContextService } from '@app/shared/services/context/system-context.service';
import { PreferenceStorageService } from '@app/shared/services/preference-storage.service';
import { delayWhenUploadIsFinished, Upload } from '@app/shared/services/upload.service';
import { PortalTranslateService } from '@app/translate/services/portal-translate.service';
import { cached, Cached, cachedGroup } from '@app/utils/cached';
import { groupBy, toMap, uniq } from '@app/utils/common';
import { DateUtils } from '@app/utils/date';
import { forkJoinRecovering } from '@app/utils/fork-join-recovering';
import { loggerFor } from '@app/utils/logger';
import { notNullish, wrapError } from '@app/utils/rxjs';
import { NotificationDialogComponent } from '@business/notification/components/notification-dialog/notification-dialog.component';
import { ReleaseNotesDialogComponent } from '@business/notification/components/release-notes-dialog/release-notes-dialog.component';
import {
  MyPortalNotificationsViewModel,
  MyPortalNotificationViewModel,
} from '@business/notification/models/my-portal-notifications-view-model';
import {
  AvailableNotificationRecipients,
  PortalNotificationDetailViewModel,
} from '@business/notification/models/portal-notification-detail-view-model';
import { getTranslationsByKey } from '@business/notification/models/portal-notification-helpers';
import { PortalNotificationState } from '@business/notification/models/portal-notification-state';
import {
  PortalNotificationsViewModel,
  PortalNotificationViewModel,
} from '@business/notification/models/portal-notifications-view-model';
import { isBefore, startOfToday } from 'date-fns';
import { combineLatest, forkJoin, iif, Observable, of } from 'rxjs';
import { delay, delayWhen, filter, first, map, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class PortalNotificationService {
  private _availableRecipients: Cached<AvailableNotificationRecipients>;

  get availableRecipients(): Cached<AvailableNotificationRecipients> {
    this._availableRecipients ??= cached({
      dataProducer: () => {
        return this.authorizationService
          .hasUserLevel$({
            requiredUserLevel: UserLevel.SystemAdmin,
          })
          .pipe(
            switchMap((isSystemAdmin) => {
              const serviceProviders$ = this.contextHelperService.reloadAndGet$(
                this.systemContextService.serviceProviders
              );

              const enterprises$ = this.contextHelperService.reloadAndGet$(
                this.serviceProviderContextService.enterprises
              );

              const productOptions$ = this.contextHelperService.reloadAndGet$(
                this.serviceProviderContextService.productOptions
              );

              return iif(
                () => isSystemAdmin,
                forkJoin([enterprises$, productOptions$, serviceProviders$]),
                forkJoin([enterprises$, productOptions$])
              );
            }),
            map(([enterprises, productOptions, serviceProviders = []]) => {
              return {
                enterprises,
                productOptions,
                serviceProviders: serviceProviders.map(({ name }) => name),
              };
            })
          );
      },
      id: 'notification-recipients',
    });

    return this._availableRecipients;
  }

  private _myNotifications: Cached<MyPortalNotificationsViewModel> | null = null;

  get myNotifications(): Cached<MyPortalNotificationsViewModel> {
    this._myNotifications ??= cached({
      dataProducer: () => {
        return this.portalNotificationRepository
          .listAllMyNotifications()
          .pipe(map((notifications) => this.mapToMyNotificationsViewModel(notifications)));
      },
      id: `my:portal-notifications`,
      reloadInterval: 60_000 * 30,
    });

    return this._myNotifications;
  }

  private _myUnseenNotifications: Cached<MyPortalNotificationsViewModel> | null = null;

  get myUnseenNotifications(): Cached<MyPortalNotificationsViewModel> {
    this._myUnseenNotifications ??= cachedGroup(
      [this.myNotifications],
      ([myNotifications]): Observable<MyPortalNotificationsViewModel> => {
        const unseenNotifications = myNotifications.notifications.filter(
          (notification) => notification.seen === false
        );

        return of({
          anyUnseen: unseenNotifications.length > 0,
          notifications: unseenNotifications,
        });
      },
      {
        id: `my:unseen-portal-notifications`,
      }
    );

    return this._myUnseenNotifications;
  }

  private _notification: Cached<PortalNotificationWithTranslations> | null = null;

  get notification(): Cached<PortalNotificationDetailViewModel> {
    this._notification ??= this.cachedHelperService.routeAwareCached({
      dataProducer: ([notificationId]) => this.portalNotificationRepository.get(Number(notificationId)),
      id: ([notificationId]) => `notification:${notificationId}`,
      routeParameters: ['notificationId'],
    });

    this._notificationViewModel ??= cachedGroup(
      [this._notification, this.availableRecipients, this.serviceProviderContextService.productOptions],
      ([notification, recipients, productOptions]) => {
        return forkJoin([
          this.portalTranslateService.languageKeys$.pipe(first()),
          this.portalDataService.translationMeta.data$.pipe(first()),
        ]).pipe(
          first(),
          map(([languages, { defaultLanguage }]) => {
            return this.toDetailViewModel(
              notification,
              languages,
              defaultLanguage,
              recipients,
              productOptions
            );
          })
        );
      },
      {
        id: 'notification:detail',
      }
    );

    return this._notificationViewModel;
  }

  private _resellerNotifications: Cached<PortalNotificationsViewModel> | null = null;

  private get resellerNotifications(): Cached<PortalNotificationsViewModel> {
    this._resellerNotifications ??= cachedGroup(
      [this.resellerNotificationsCached, this.portalDataService.translationMeta, this.myNotifications],
      ([notifications, { defaultLanguage }, myNotifications]) => {
        return of({
          canCopyReceivedNotifications: myNotifications.notifications.length > 0,
          defaultLanguage,
          notifications: notifications.map((notification) => this.toOverviewViewModel(notification)),
        } satisfies PortalNotificationsViewModel);
      },
      {
        id: 'reseller:notifications',
      }
    );

    return this._resellerNotifications;
  }

  private _resellerNotificationsCached: Cached<PortalNotification[]> | null = null;

  private get resellerNotificationsCached(): Cached<PortalNotification[]> {
    this._resellerNotificationsCached ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () => this.portalNotificationRepository.list(UserLevel.ServiceProviderAdmin),
      id: ([serviceProviderId]) => `service-provider:${serviceProviderId}:notifications`,
      routeParameters: ['serviceProviderId'],
    });

    return this._resellerNotificationsCached;
  }

  private _systemNotifications: Cached<PortalNotificationsViewModel> | null = null;

  private get systemNotifications(): Cached<PortalNotificationsViewModel> {
    this._systemNotifications ??= cachedGroup(
      [this.systemNotificationsCached, this.portalDataService.translationMeta],
      ([notifications, { defaultLanguage }]) => {
        return of({
          canCopyReceivedNotifications: false,
          defaultLanguage,
          notifications: notifications.map((notification) => this.toOverviewViewModel(notification)),
        });
      },
      {
        id: 'system:notifications-view-model',
      }
    );

    return this._systemNotifications;
  }

  private _systemNotificationsCached: Cached<PortalNotification[]> | null = null;

  private get systemNotificationsCached(): Cached<PortalNotification[]> {
    this._systemNotificationsCached ??= cached({
      dataProducer: () => this.portalNotificationRepository.list(UserLevel.SystemAdmin),
      id: `system:notifications`,
    });

    return this._systemNotificationsCached;
  }

  get notifications(): Cached<PortalNotificationsViewModel> {
    if (this.menuService.isActive(UserLevel.ServiceProviderAdmin)) {
      return this.resellerNotifications;
    } else {
      return this.systemNotifications;
    }
  }

  private _notificationViewModel: Cached<PortalNotificationDetailViewModel> | null = null;
  private alreadyShown: number[] = [];
  private log = loggerFor('PortalNotificationService');

  constructor(
    private applicationEventService: ApplicationEventService,
    private authenticationService: AuthenticationService,
    private authorizationService: AuthorizationService,
    private cachedHelperService: CachedHelperService,
    private contextHelperService: ContextHelperService,
    private dialogService: DialogService,
    private eventSourceService: EventSourceService,
    private menuService: MenuService,
    private notificationService: NotificationService,
    private portalDataService: PortalDataService,
    private portalNotificationRepository: PortalNotificationRepository,
    private portalTranslateService: PortalTranslateService,
    private preferenceStorageService: PreferenceStorageService,
    private serviceProviderContextService: ServiceProviderContextService,
    private systemContextService: SystemContextService,
    private toastService: ToastService
  ) {}

  copy(update: PortalCopyNotification): Observable<void> {
    return this.portalNotificationRepository.copy(update).pipe(
      delayWhen(() => this.notifications.reload()),
      wrapError('portal-notification-service:copy-from-system')
    );
  }

  create(update: PortalNotificationUpdate): Observable<number> {
    return this.portalNotificationRepository.create(update).pipe(
      delayWhen(() => this.notifications.reload()),
      wrapError('portal-notification-service:create')
    );
  }

  delete(notificationId: number): Observable<void> {
    return this.portalNotificationRepository.delete(notificationId).pipe(
      delayWhen(() => this.notifications.reload()),
      wrapError('portal-notification-service:delete')
    );
  }

  deleteMyNotification(notificationId: number): Observable<void> {
    return this.portalNotificationRepository.deleteMyNotification(notificationId).pipe(
      delayWhen(() => forkJoin([this.myUnseenNotifications.reload(), this.myNotifications.reload()])),
      wrapError('portal-notification-service:delete-my-notification')
    );
  }

  deleteTranslation(notificationId: number, translationId: number): Observable<void> {
    return this.portalNotificationRepository.deleteTranslation(notificationId, translationId).pipe(
      delayWhen(() => this.notification.reload()),
      wrapError('portal-notification-service:delete-translation')
    );
  }

  deleteTranslationsByLanguage(notificationId: number, language: string): Observable<void> {
    return this.portalNotificationRepository.deleteTranslationsByLanguage(notificationId, language).pipe(
      delayWhen(() => this.notification.reload()),
      wrapError('portal-notification-service:delete-translations-by-language')
    );
  }

  expireNotification(notificationId: number, date?: Date): Observable<void> {
    return this.portalNotificationRepository.expire(notificationId, date).pipe(
      delayWhen(() => forkJoin([this.notification.reload(), this.notifications.reload()])),
      wrapError('portal-notification-service:expire-notification')
    );
  }

  getAvailableRecipients(): Observable<PortalNotificationRecipient[]> {
    return this.menuService.menuData$.pipe(
      map((menuData) => {
        return menuData.activeUserLevels.isServiceProvider
          ? [PortalNotificationRecipient.ENTERPRISE]
          : [PortalNotificationRecipient.SERVICE_PROVIDER, PortalNotificationRecipient.ENTERPRISE];
      })
    );
  }

  initialize(): void {
    // Show unread important notifications on login
    this.subscribeIfUserLevelIsEnough(this.getUnseenNotifications()).subscribe(
      ({ notifications, language }) => {
        this.handleUnseenNotifications(notifications, language);
      }
    );

    this.authenticationService.authUserCached.data$
      .pipe(
        switchMap((authUser) => {
          if (authUser != null) {
            const observe: Omit<PortalEventSubject, 'system'> = {
              context: PortalEventContext.NOTIFICATION_UPDATE,
              serviceProvider: authUser.serviceProviderName,
            };

            if (this.authenticationService.userIsEnterpriseAdmin()) {
              observe.enterprise = authUser.enterpriseName;
            } else if (this.authenticationService.userIsEndUser()) {
              observe.endUser = authUser.username;
              observe.enterprise = authUser.enterpriseName;
            }

            return this.eventSourceService.observe(observe);
          } else {
            return of(null);
          }
        }),
        filter((event) => event != null)
      )
      .subscribe((event) => {
        const data = event.data as Record<string, any>;
        this.log.info(`New notification is available. notificationId=${data.notificationId}`);
        this.applicationEventService.emit(NewUnseenNotification({}));
      });

    this.applicationEventService.on$(NewUnseenNotification).subscribe(({ payload: { since } }) => {
      this.myUnseenNotifications.reload();
      this.myNotifications.reload();

      this.getUnseenNotifications(since).subscribe(({ notifications, language }) => {
        this.handleUnseenNotifications(notifications, language);
      });
    });

    this.authenticationService.onLogout$.subscribe(() => {
      this.alreadyShown = [];
    });
  }

  listRecipients(notificationId: number): Observable<PortalNotificationEnhancedRecipient[]> {
    return this.portalNotificationRepository
      .listRecipients(notificationId)
      .pipe(wrapError('portal-notification-service:list-recipients'));
  }

  markAllAsSeen(): Observable<void> {
    return this.portalNotificationRepository.markAllAsSeen().pipe(
      delayWhen(() => forkJoin([this.myNotifications.reload(), this.myUnseenNotifications.reload()])),
      wrapError('portal-notification-service:mark-all-as-seen')
    );
  }

  markAsSeen(notificationId: number): Observable<void> {
    return this.portalNotificationRepository.markAsSeen(notificationId).pipe(
      delayWhen(() => forkJoin([this.myUnseenNotifications.reload(), this.myNotifications.reload()])),
      wrapError('portal-notification-service:mark-as-seen')
    );
  }

  publish(notificationId: number, date: Date): Observable<void> {
    return this.portalNotificationRepository.publish(notificationId, date).pipe(
      delayWhen(() => forkJoin([this.notification.reload(), this.notifications.reload()])),
      wrapError('portal-notification-service:publish')
    );
  }

  render(notificationId: number, language: string): Observable<RenderedPortalNotification> {
    return this.portalNotificationRepository
      .render(notificationId, language)
      .pipe(wrapError('portal-notification-service:render'));
  }

  replaceTranslations(notificationId: number, from: string, to: string): Observable<void> {
    return this.portalNotificationRepository.copyTranslations(notificationId, from, to).pipe(
      delayWhen(() => this.notification.reload()),
      wrapError('portal-notification-service:replace-translations')
    );
  }

  saveTranslation({
    file,
    notificationId,
    translationId,
    update,
  }: SaveTranslationOptions): Observable<Upload<void>> {
    return iif(
      () => translationId != null,
      this.portalNotificationRepository.updateTranslation({
        file,
        notificationId,
        translationId: translationId!,
        update,
      }),
      this.portalNotificationRepository.createTranslation({
        file,
        notificationId,
        update,
      })
    ).pipe(
      delayWhenUploadIsFinished(() => this.notification.reload()),
      wrapError('portal-notification-service:save-translation')
    );
  }

  sendTestMail(notificationId: number, data: PortalNotificationSendTestMail): Observable<void> {
    return this.portalNotificationRepository
      .sendTestMail(notificationId, data)
      .pipe(wrapError('portal-notification-service:send-test-mail'));
  }

  unpublish(notificationId: number): Observable<void> {
    return this.portalNotificationRepository.unpublish(notificationId).pipe(
      delayWhen(() => forkJoin([this.notification.reload(), this.notifications.reload()])),
      wrapError('portal-notification-service:unpublish')
    );
  }

  update(notificationId: number, update: PortalNotificationUpdate): Observable<void> {
    return this.portalNotificationRepository.update(notificationId, update).pipe(
      delayWhen(() => this.notifications.reload()),
      wrapError('portal-notification-service:update')
    );
  }

  updatePublishedNotification(
    notificationId: number,
    update: PortalPublishedNotificationUpdate
  ): Observable<void> {
    return this.portalNotificationRepository.updatePublishedNotification(notificationId, update).pipe(
      delayWhen(() => this.notifications.reload()),
      wrapError('portal-notification-service:update-published-notification')
    );
  }

  updateTranslationOrder(
    notificationId: number,
    update: PortalNotificationTranslationOrderUpdate
  ): Observable<void> {
    return this.portalNotificationRepository.updateTranslationOrder(notificationId, update).pipe(
      delayWhen(() => this.notification.reload()),
      wrapError('portal-notification-service:update-translation-order')
    );
  }

  private calculateNotificationState(expirationDate: Date, publicationDate: Date): PortalNotificationState {
    if (publicationDate == null) {
      return PortalNotificationState.Draft;
    }

    if (isBefore(expirationDate, new Date())) {
      return PortalNotificationState.Expired;
    }

    if (isBefore(publicationDate, new Date())) {
      return PortalNotificationState.Public;
    }

    return PortalNotificationState.Scheduled;
  }

  private getUnseenNotifications(since?: Date) {
    this.log.debug('Checking for new important unseen notifications');

    return combineLatest([
      this.myUnseenNotifications.data$,
      this.portalTranslateService.activeLanguage$.pipe(filter((language) => language != null)),
      this.preferenceStorageService.preference$('closed-notifications', [] as number[]),
    ]).pipe(
      first(),
      map(([{ notifications }, language, closedNotifications]) => {
        return {
          language,
          notifications: notifications.filter((notification) => {
            const important = notification.important;
            const closedBefore = closedNotifications.includes(notification.id);
            const isNew = since != null ? isBefore(notification.publishedAt, since) === false : true;
            const isReleaseNotes = notification.type === PortalNotificationType.RELEASE_NOTES;

            return (important && closedBefore === false && isNew) || isReleaseNotes;
          }),
        };
      })
    );
  }

  private handleUnseenNotifications(notifications: MyPortalNotificationViewModel[], language: string): void {
    const { releaseNotes = [], important = [] } = groupBy(notifications, (notification) =>
      notification.type === PortalNotificationType.RELEASE_NOTES ? 'releaseNotes' : 'important'
    );

    this.showReleaseNotes(releaseNotes);
    this.showImportantNotifications(important, language);
  }

  private mapToMyNotificationsViewModel(
    notifications: MyPortalNotification[]
  ): MyPortalNotificationsViewModel {
    const notificationViewModels = notifications.map((notification): MyPortalNotificationViewModel => {
      const { title = [], content = [] } = groupBy(notification.translations, (translation) =>
        translation.orderIndex === 0 ? 'title' : 'content'
      );

      return {
        _original: notification,
        beginAt: DateUtils.parse(notification.beginAt),
        canDelete:
          notification.type !== PortalNotificationType.MAINTENANCE || notification.important === false,
        endAt: DateUtils.parse(notification.endAt),
        id: notification.id,
        important: notification.important,
        name: notification.name,
        publishedAt: DateUtils.parse(notification.publishedAt),
        seen: notification.seenAt != null,
        seenAt: DateUtils.parse(notification.seenAt),
        titles: toMap(title, 'language'),
        translations: groupBy(content, (translation) => translation.language),
        type: notification.type,
        updatedAt: DateUtils.parse(notification.updatedAt),
      };
    });

    return {
      anyUnseen: notificationViewModels.some((notification) => notification.seen === false),
      notifications: notificationViewModels,
    };
  }

  private showImportantNotifications(important: MyPortalNotificationViewModel[], language: string): void {
    important
      .filter(
        (notification) =>
          this.alreadyShown.includes(notification.id) === false &&
          notification.type !== PortalNotificationType.MAINTENANCE
      )
      .forEach((notification) => this.showNotificationInToast(notification, language));

    this.alreadyShown.push(...important.map((notification) => notification.id));
  }

  private showNotificationInToast(notification: MyPortalNotificationViewModel, language: string): void {
    this.toastService.show({
      actions: [
        {
          invoke: (toast) => {
            toast.hide();

            this.dialogService.open(NotificationDialogComponent).withInput({
              notificationId: notification.id,
              publicationDate: notification.publishedAt,
              translations: notification._original.translations,
              type: notification.type,
            });
          },
          label: 'myNotifications.showNotification',
          variant: 'primary',
        },
        {
          invoke: (toast) => toast.hide(),
          label: 'common.ignore',
          variant: 'link',
        },
      ],
      body: getTranslationsByKey(notification.titles, language).title,
      header: `myNotifications.newNotification.${notification.type}`,
      onClose: () => {
        this.preferenceStorageService.update<number[]>('closed-notifications', (value = []) =>
          value.concat(notification.id)
        );
      },
      stay: true,
    });
  }

  private showReleaseNotes(notifications: MyPortalNotificationViewModel[]): void {
    if (notifications.length > 0) {
      this.dialogService
        .open(ReleaseNotesDialogComponent, {})
        .withInput({
          notifications,
        })
        .onDismiss(() => {
          const updates$ = notifications.map((notification) => this.markAsSeen(notification.id));

          forkJoinRecovering(updates$).subscribe(({ errors }) => {
            errors.forEach((error) => {
              this.notificationService.showError(error);
            });
          });
        });
    }
  }

  private subscribeIfUserLevelIsEnough<T>(observable$: Observable<T>): Observable<T> {
    return this.authenticationService.isAuthenticated$.pipe(
      delay(0),
      switchMap((isAuthenticated) => {
        if (isAuthenticated) {
          return this.authenticationService.authUser$;
        } else {
          return of(null);
        }
      }),
      switchMap((authUser) => {
        if (userLevelIsEnough(UserLevel.EnterpriseAdmin, authUser)) {
          return observable$;
        } else {
          return of(null);
        }
      }),
      notNullish()
    );
  }

  private toDetailViewModel(
    notification: PortalNotificationWithTranslations,
    languages: string[],
    defaultLanguage: string,
    recipients: AvailableNotificationRecipients,
    productOptions: ProductOption[]
  ): PortalNotificationDetailViewModel {
    const expirationDate = DateUtils.parse(notification.expiresAt);
    const publicationDate = DateUtils.parse(notification.publishedAt);
    const state = this.calculateNotificationState(expirationDate, publicationDate);
    const { id, important, name, publishedAt, translations } = notification;
    const translatedLanguages = uniq(translations.map((translation) => translation.language));

    return {
      _original: notification,
      allTranslationsAvailable: languages.every((language) =>
        translations.some((translation) => translation.language === language)
      ),
      availableLanguages: languages
        .filter((language) => translations.some((translation) => translation.language === language) === false)
        .sort((a, b) => a.localeCompare(b)),
      availableRecipients: recipients,
      defaultLanguage,
      expiryDateInPast: DateUtils.isBefore(expirationDate, startOfToday()),
      hasMultipleTranslations: translatedLanguages.length > 1,
      hasPublicationDate: publishedAt != null,
      id,
      important,
      isExpired: state === PortalNotificationState.Expired,
      isPublished: state === PortalNotificationState.Public || state === PortalNotificationState.Expired,
      languages,
      name,
      noTranslationAvailable: translations.length === 0,
      publicationDate,
      sendMail: notification.sendMail,
      state,
      translatedLanguages,
      translations: {
        all: translations,
        contentMap: groupBy(
          translations.filter((translation) => translation.orderIndex > 0),
          (translation) => translation.language
        ),
        titles: translations.filter((translation) => translation.orderIndex === 0),
        titleMap: groupBy(
          translations.filter((translation) => translation.orderIndex === 0),
          (translation) => translation.language,
          (translations) => translations[0]
        ),
      },
      type: notification.type,
    };
  }

  private toOverviewViewModel(notification: PortalNotification): PortalNotificationViewModel {
    const expirationDate = DateUtils.parse(notification.expiresAt);
    const publicationDate = DateUtils.parse(notification.publishedAt);

    return {
      _original: notification,
      expiresAt: expirationDate,
      id: notification.id,
      important: notification.important,
      name: notification.name,
      publishedAt: publicationDate,
      recipient: notification.recipient,
      showToAll: notification.showToAll,
      state: this.calculateNotificationState(expirationDate, publicationDate),
      type: notification.type,
      updatedBy: notification.updatedBy,
    };
  }
}

interface SaveTranslationOptions {
  file?: File;
  notificationId: number;
  translationId?: number;
  update: PortalNotificationTranslationUpdate;
}
